import { openDB, type IDBPDatabase } from 'idb'
import type { ConfigResponse } from './api-types'

/**
 * Local device store.
 *
 * Holds the device token, the last known workflow config, and per-device
 * preferences. The cached config is what lets the app render a usable workflow
 * list on the very first frame instead of waiting for the network — the old
 * build gated its whole UI on the first Firestore snapshot, which is why it
 * showed a white screen for seconds.
 */

const DB_NAME = 'scanflow'
const DB_VERSION = 1
const STORE = 'kv'

const KEY_ADMIN_TOKEN = 'admin-token'
const KEY_CONFIG = 'config'
const KEY_PREFS = 'prefs'

export interface DevicePrefs {
  activeWorkflowId: string | null
  tagFilter: string | null
  soundOn: boolean
}

export const DEFAULT_PREFS: DevicePrefs = {
  activeWorkflowId: null,
  tagFilter: null,
  soundOn: true,
}

export interface CachedConfig {
  config: ConfigResponse
  fetchedAt: number
}

let dbPromise: Promise<IDBPDatabase> | null = null

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE)) database.createObjectStore(STORE)
    },
  })
  return dbPromise
}

/**
 * Every read swallows its error and returns a fallback.
 *
 * IndexedDB is genuinely unavailable in some states — Safari private browsing,
 * a corrupted store, storage pressure. None of those should stop the app
 * starting; they should degrade it to "behaves like a fresh device".
 */
async function get<T>(key: string, fallback: T): Promise<T> {
  try {
    const value = (await (await db()).get(STORE, key)) as T | undefined
    return value ?? fallback
  } catch {
    return fallback
  }
}

async function set(key: string, value: unknown): Promise<void> {
  try {
    await (await db()).put(STORE, value, key)
  } catch {
    /* non-fatal: the app works, it just won't remember across launches */
  }
}

async function del(key: string): Promise<void> {
  try {
    await (await db()).delete(STORE, key)
  } catch {
    /* ignore */
  }
}

// Memoised after first load rather than hitting IndexedDB on every admin call.
let adminTokenCache: string | null | undefined

/**
 * The admin session token.
 *
 * Only ever sent to /api/admin/*. The scanner side of the app is open and needs
 * no credential at all, so an ordinary user's device stores nothing.
 */
export async function getAdminToken(): Promise<string | null> {
  if (adminTokenCache !== undefined) return adminTokenCache
  adminTokenCache = await get<string | null>(KEY_ADMIN_TOKEN, null)
  return adminTokenCache
}

export async function setAdminToken(token: string): Promise<void> {
  adminTokenCache = token
  await set(KEY_ADMIN_TOKEN, token)
}

export async function clearAdminToken(): Promise<void> {
  adminTokenCache = null
  await del(KEY_ADMIN_TOKEN)
}

export async function getCachedConfig(): Promise<CachedConfig | null> {
  return get<CachedConfig | null>(KEY_CONFIG, null)
}

export async function setCachedConfig(config: ConfigResponse): Promise<void> {
  await set(KEY_CONFIG, { config, fetchedAt: Date.now() } satisfies CachedConfig)
}

export async function getPrefs(): Promise<DevicePrefs> {
  const stored = await get<Partial<DevicePrefs>>(KEY_PREFS, {})
  return { ...DEFAULT_PREFS, ...stored }
}

export async function setPrefs(prefs: Partial<DevicePrefs>): Promise<DevicePrefs> {
  const next = { ...(await getPrefs()), ...prefs }
  await set(KEY_PREFS, next)
  return next
}
